import { Link } from 'react-router-dom';
import { useAuth, useAdminDashboardStats } from '@theraply/api-client';

function formatRevenue(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useAdminDashboardStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to load dashboard statistics'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
      <p className="mt-1 text-sm text-gray-500">
        Welcome back, {user?.firstName}. Here is your system overview.
      </p>

      {/* KPI Cards */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="text-sm font-medium text-gray-500 truncate">Total Consumers</div>
            <div className="mt-2 text-3xl font-bold text-blue-600">
              {data?.totalConsumers?.toLocaleString() ?? 0}
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="text-sm font-medium text-gray-500 truncate">Total Therapists</div>
            <div className="mt-2 text-3xl font-bold text-green-600">
              {data?.totalTherapists?.toLocaleString() ?? 0}
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="text-sm font-medium text-gray-500 truncate">Completed Sessions</div>
            <div className="mt-2 text-3xl font-bold text-purple-600">
              {data?.completedBookings?.toLocaleString() ?? 0}
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="text-sm font-medium text-gray-500 truncate">Total Revenue</div>
            <div className="mt-2 text-3xl font-bold text-emerald-600">
              {formatRevenue(data?.totalRevenueCents ?? 0)}
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="text-sm font-medium text-gray-500 truncate">Pending Verifications</div>
            <div className="mt-2 text-3xl font-bold text-orange-600">
              {data?.pendingVerifications?.toLocaleString() ?? 0}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link
            to="/verifications"
            className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow flex items-center space-x-3"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-md bg-indigo-500 flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Review Verifications</div>
              <div className="text-sm text-gray-500">{data?.pendingVerifications ?? 0} pending</div>
            </div>
          </Link>

          <Link
            to="/users"
            className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow flex items-center space-x-3"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-md bg-blue-500 flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m-3-3.75a4 4 0 100-8 4 4 0 000 8z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Manage Users</div>
              <div className="text-sm text-gray-500">{data?.totalConsumers ?? 0} consumers</div>
            </div>
          </Link>

          <Link
            to="/therapists"
            className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow flex items-center space-x-3"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-md bg-green-500 flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Manage Therapists</div>
              <div className="text-sm text-gray-500">{data?.totalTherapists ?? 0} therapists</div>
            </div>
          </Link>

          <Link
            to="/system"
            className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow flex items-center space-x-3"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-md bg-gray-500 flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">System Health</div>
              <div className="text-sm text-gray-500">Monitor status</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
